import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Card, CardContent, Tabs, Tab, Chip, Grid, Table, TableHead,
  TableRow, TableCell, TableBody, Button, CircularProgress, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Alert, Divider, Stack, Avatar,
  FormControl, InputLabel, Select, MenuItem, RadioGroup, FormControlLabel, Radio, Switch,
} from '@mui/material';
import {
  ArrowBack, Router as RouterIcon, Security, Badge, Dialpad, Inventory2, ReceiptLong,
  Call, AccountBalance, Add, Delete, Refresh, VpnKey, CheckCircle, Cancel, Business, Edit, BugReport, PhoneAndroid,
  VerifiedUser, UploadFile, Download,
} from '@mui/icons-material';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../services/api';
import { DirectMediaTab } from './DirectMediaTab';
import { SipTraceModal } from '../../components/SipTraceModal';
import { financialHidden } from '../../store/auth';
import { useAuthStore } from '../../store/auth';

const money = (v: any) => (v === null || v === undefined) ? '—' : '$' + Number(v).toFixed(2);
const StatusChip = ({ s }: { s: string }) => <Chip label={s} size="small" color={s === 'active' ? 'success' : s === 'suspended' ? 'warning' : 'default'} />;

export const CustomerDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const me = useAuthStore(s => s.user);
  const hideMoney = financialHidden(me?.role);
  const [tab, setTab] = useState(0);
  const [tenant, setTenant] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  // per-tab data
  const [trunk, setTrunk] = useState<any[]>([]);
  const [acl, setAcl] = useState<any[]>([]);
  const [dids, setDids] = useState<any[]>([]);
  const [products, setProducts] = useState<any[]>([]);
  const [invoices, setInvoices] = useState<any[]>([]);
  const [cdrs, setCdrs] = useState<any[]>([]);
  const [balance, setBalance] = useState<any>(null);
  const [txns, setTxns] = useState<any[]>([]);
  const [aclDialog, setAclDialog] = useState(false);
  const [newIp, setNewIp] = useState({ ip_addr: '', mask: 32, description: '' });
  const [err, setErr] = useState('');
  const [cli, setCli] = useState<any>({ pai_policy: 'pool', pai_fixed_did: '' });
  const [cliMsg, setCliMsg] = useState('');
  const [overrides, setOverrides] = useState<any[]>([]);
  const [ovNew, setOvNew] = useState<any>({ dst_prefix: '', description: '' });
  const [ovMsg, setOvMsg] = useState('');
  const [didMsg, setDidMsg] = useState('');
  const [destEdits, setDestEdits] = useState<Record<string, string>>({});
  const [traceId, setTraceId] = useState<string | null>(null);
  const [traceOpen, setTraceOpen] = useState(false);
  // SIP devices
  const [devices, setDevices] = useState<any[]>([]);
  const [devDialog, setDevDialog] = useState(false);
  const [newDev, setNewDev] = useState<any>({ caller_number: '', voicemail: false });
  const [devCreds, setDevCreds] = useState<any>(null);
  const [devErr, setDevErr] = useState('');
  const [showPw, setShowPw] = useState<Record<string, boolean>>({});
  // customer trunk (ingress) add
  const [trunkDialog, setTrunkDialog] = useState(false);
  const [newTrunk, setNewTrunk] = useState<any>({ name: '', host: '', port: 5060, protocol: 'UDP', auth_type: 'ip', username: '', password: '', srtp: 'none' });
  const [trunkErr, setTrunkErr] = useState('');
  // edit customer fields
  const [editDialog, setEditDialog] = useState(false);
  const [editForm, setEditForm] = useState<any>({});
  const [editErr, setEditErr] = useState('');
  const [ratePlans, setRatePlans] = useState<any[]>([]);
  const [routingProfiles, setRoutingProfiles] = useState<any[]>([]);
  // KYC / Compliance
  const KYC_TAB = 10;
  const [kyc, setKyc] = useState<any>(null);
  const [kycForm, setKycForm] = useState<any>({});
  const [kycMsg, setKycMsg] = useState('');
  const [kycUpOpen, setKycUpOpen] = useState(false);
  const [kycUp, setKycUp] = useState<any>({ doc_type: 'business_registration', title: '', issued_date: '', expiry_date: '' });
  const [kycFile, setKycFile] = useState<File | null>(null);
  const [kycUpErr, setKycUpErr] = useState('');
  const [kycReviewDoc, setKycReviewDoc] = useState<any>(null);
  const [kycReview, setKycReview] = useState<any>({ status: 'approved', review_notes: '', expiry_date: '' });

  const loadTenant = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get(`/tenants/${id}`); setTenant(data); }
    catch { setTenant(null); } finally { setLoading(false); }
  }, [id]);

  useEffect(() => { loadTenant(); }, [loadTenant]);

  // lazy-load per tab
  useEffect(() => {
    if (!id) return;
    const g = (u: string, set: (v: any[]) => void, key = 'data') =>
      api.get(u).then(r => set(Array.isArray(r.data) ? r.data : (r.data?.[key] || []))).catch(() => set([]));
    if (tab === 1) g(`/tenants/${id}/trunk`, setTrunk);
    if (tab === 2) g(`/tenants/${id}/acl`, setAcl);
    if (tab === 3 || tab === 4) g(`/numbers?tenant_id=${id}&limit=500`, setDids);
    if (tab === 3) api.get(`/tenants/${id}/cli-policy`).then(r => setCli({ pai_policy: r.data.pai_policy || 'pool', pai_fixed_did: r.data.pai_fixed_did || '' })).catch(() => {});
    if (tab === 3) api.get(`/tenants/${id}/cli-overrides`).then(r => setOverrides(r.data || [])).catch(() => {});
    if (tab === 5) api.get(`/subscriptions/customer?tenant_id=${id}`).then(r => setProducts(r.data?.data || r.data || [])).catch(() => setProducts([]));
    if (tab === 6) g(`/invoices?tenant_id=${id}`, setInvoices);
    if (tab === 7) g(`/cdr?tenant_id=${id}&limit=100`, setCdrs);
    if (tab === 9) g(`/tenants/${id}/devices`, setDevices);
    if (tab === 8) {
      api.get(`/billing/balance/${id}`).then(r => setBalance(r.data)).catch(() => setBalance(null));
      g(`/billing/transactions?tenant_id=${id}`, setTxns);
      g(`/invoices?tenant_id=${id}`, setInvoices);
    }
    if (tab === KYC_TAB) loadKyc();
  }, [tab, id]);

  // deep link from the global Compliance dashboard: /customers/:id?tab=kyc
  useEffect(() => { if (searchParams.get('tab') === 'kyc') setTab(KYC_TAB); }, [searchParams]);

  const addAcl = async () => {
    setErr('');
    try { await api.post(`/tenants/${id}/acl`, newIp); setAclDialog(false); setNewIp({ ip_addr: '', mask: 32, description: '' }); api.get(`/tenants/${id}/acl`).then(r => setAcl(r.data)); }
    catch (e: any) { setErr(e.response?.data?.message || 'Add failed'); }
  };
  const delAcl = async (aclId: number) => {
    if (!window.confirm('Remove this trusted IP?')) return;
    await api.delete(`/tenants/${id}/acl/${aclId}`); api.get(`/tenants/${id}/acl`).then(r => setAcl(r.data));
  };
  const loadOverrides = async () => {
    try { const { data } = await api.get(`/tenants/${id}/cli-overrides`); setOverrides(data || []); } catch { /* ignore */ }
  };
  const addOverride = async () => {
    setOvMsg('');
    try {
      await api.post(`/tenants/${id}/cli-overrides`, { dst_prefix: ovNew.dst_prefix, description: ovNew.description || undefined, mode: 'pai' });
      setOvNew({ dst_prefix: '', description: '' }); setOvMsg('Override added ✓'); loadOverrides();
    } catch (e: any) { setOvMsg(e.response?.data?.message || 'Add failed'); }
  };
  const toggleOverride = async (o: any) => {
    setOvMsg('');
    try { await api.patch(`/tenants/${id}/cli-overrides/${o.id}`, { active: !o.active }); setOvMsg(`Prefix ${o.dst_prefix} ${!o.active ? 'enabled' : 'disabled'} ✓`); loadOverrides(); }
    catch (e: any) { setOvMsg(e.response?.data?.message || 'Update failed'); }
  };
  const removeOverride = async (o: any) => {
    if (!window.confirm(`Remove the override for prefix ${o.dst_prefix}? Calls will present the customer's own From again.`)) return;
    setOvMsg('');
    try { await api.delete(`/tenants/${id}/cli-overrides/${o.id}`); setOvMsg(`Prefix ${o.dst_prefix} removed ✓`); loadOverrides(); }
    catch (e: any) { setOvMsg(e.response?.data?.message || 'Remove failed'); }
  };

  const saveCli = async () => {
    setCliMsg('');
    try {
      const { data } = await api.patch(`/tenants/${id}/cli-policy`, { policy: cli.pai_policy, fixed_did: cli.pai_fixed_did || undefined });
      setCli({ pai_policy: data.pai_policy, pai_fixed_did: data.pai_fixed_did || '' });
      setCliMsg('Saved ✓');
    } catch (e: any) { setCliMsg(e.response?.data?.message || 'Save failed'); }
  };
  const reloadTrunk = () => api.get(`/tenants/${id}/trunk`).then(r => setTrunk(Array.isArray(r.data) ? r.data : (r.data?.data || []))).catch(() => {});
  const addTrunk = async () => {
    setTrunkErr('');
    try {
      await api.post(`/tenants/${id}/trunk`, newTrunk);
      setTrunkDialog(false);
      setNewTrunk({ name: '', host: '', port: 5060, protocol: 'UDP', auth_type: 'ip', username: '', password: '', srtp: 'none' });
      reloadTrunk();
    } catch (e: any) { setTrunkErr(e.response?.data?.message || 'Create failed'); }
  };
  const delTrunk = async (carrierId: string) => {
    if (!window.confirm('Remove this customer trunk? Its routing + firewall trust will be revoked.')) return;
    await api.delete(`/tenants/${id}/trunk/${carrierId}`); reloadTrunk();
  };
  const reloadDevices = () => api.get(`/tenants/${id}/devices`).then(r => setDevices(Array.isArray(r.data) ? r.data : (r.data?.data || []))).catch(() => {});
  const addDevice = async () => {
    setDevErr('');
    try {
      const { data } = await api.post(`/tenants/${id}/devices`, newDev);
      setDevCreds(data); setDevDialog(false); setNewDev({ caller_number: '', voicemail: false });
      reloadDevices();
    } catch (e: any) { setDevErr(e.response?.data?.message || 'Create failed'); }
  };
  const delDevice = async (deviceId: string) => {
    if (!window.confirm('Delete this SIP device? The account can no longer register.')) return;
    await api.delete(`/tenants/${id}/devices/${deviceId}`); reloadDevices();
  };
  const toggleDevice = async (d: any) => {
    await api.patch(`/tenants/${id}/devices/${d.id}`, { status: d.status === 'active' ? 'disabled' : 'active' });
    reloadDevices();
  };
  // ── KYC / Compliance ──────────────────────────────────────────────────
  const loadKyc = async () => {
    try {
      const { data } = await api.get(`/compliance/${id}`);
      setKyc(data);
      const p = data.profile || {};
      setKycForm({
        legal_entity_name: p.legal_entity_name || '', registration_number: p.registration_number || '',
        incorporation_country: p.incorporation_country || '', tax_id: p.tax_id || '',
        ocn: p.ocn || '', fcc_499_id: p.fcc_499_id || '', rmd_registered: !!p.rmd_registered,
        traceback_contact_name: p.traceback_contact_name || '', traceback_contact_email: p.traceback_contact_email || '',
        traceback_contact_phone: p.traceback_contact_phone || '', origination_countries: p.origination_countries || '',
        no_spoofing_attested: !!p.no_spoofing_attested, kyc_status: p.kyc_status || 'not_started',
        kyc_expiry: p.kyc_expiry ? String(p.kyc_expiry).slice(0, 10) : '', notes: p.notes || '',
      });
    } catch { setKyc(null); }
  };
  const saveKycProfile = async () => {
    setKycMsg('');
    try { await api.put(`/compliance/${id}`, kycForm); setKycMsg('Saved ✓'); loadKyc(); }
    catch (e: any) { setKycMsg(e.response?.data?.message || 'Save failed'); }
  };
  const doUploadKyc = async () => {
    setKycUpErr('');
    if (!kycFile) { setKycUpErr('Choose a file'); return; }
    try {
      const fd = new FormData();
      fd.append('file', kycFile);
      fd.append('doc_type', kycUp.doc_type);
      if (kycUp.title) fd.append('title', kycUp.title);
      if (kycUp.issued_date) fd.append('issued_date', kycUp.issued_date);
      if (kycUp.expiry_date) fd.append('expiry_date', kycUp.expiry_date);
      await api.post(`/compliance/${id}/documents`, fd);
      setKycUpOpen(false); setKycFile(null);
      setKycUp({ doc_type: 'business_registration', title: '', issued_date: '', expiry_date: '' });
      loadKyc();
    } catch (e: any) { setKycUpErr(e.response?.data?.message || 'Upload failed'); }
  };
  const downloadKycDoc = async (doc: any) => {
    try {
      const res = await api.get(`/compliance/documents/${doc.id}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = doc.file_name || 'document';
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { setKycMsg('Download failed'); }
  };
  const openKycReview = (doc: any, preset?: string) => {
    setKycMsg('');
    setKycReviewDoc(doc);
    setKycReview({ status: preset || 'approved', review_notes: doc.review_notes || '', expiry_date: doc.expiry_date ? String(doc.expiry_date).slice(0, 10) : '' });
  };
  const submitKycReview = async () => {
    try { await api.patch(`/compliance/documents/${kycReviewDoc.id}/review`, kycReview); setKycReviewDoc(null); loadKyc(); }
    catch (e: any) { setKycMsg(e.response?.data?.message || 'Review failed'); }
  };
  const deleteKycDoc = async (doc: any) => {
    if (!window.confirm(`Delete document "${doc.file_name}"?`)) return;
    await api.delete(`/compliance/documents/${doc.id}`); loadKyc();
  };
  const DOC_TYPE_LABELS: Record<string, string> = {
    business_registration: 'Business registration', beneficial_ownership_id: 'Beneficial-owner ID',
    number_authorization_loa: 'Number authorization / LOA', regulatory_license: 'Regulatory license',
    robocall_mitigation_statement: 'Robocall-mitigation statement', signed_service_agreement: 'Signed service agreement',
    tax_certificate: 'Tax certificate', other: 'Other',
  };
  const KYC_STATUS_COLOR: Record<string, any> = { approved: 'success', in_review: 'warning', rejected: 'error', not_started: 'default' };
  const DOC_STATUS_COLOR: Record<string, any> = { approved: 'success', pending: 'warning', rejected: 'error', expired: 'error' };
  const openEdit = async () => {
    setEditErr('');
    setEditForm({
      name: tenant.name || '', company_name: tenant.company_name || '', contact_email: tenant.contact_email || '',
      contact_phone: tenant.contact_phone || '', city: tenant.city || '', billing_type: tenant.billing_type || 'postpaid',
      currency: tenant.currency || 'USD', credit_limit: tenant.credit_limit ?? 0, status: tenant.status || 'active',
      rate_plan_id: tenant.rate_plan_id || '', routing_profile_id: tenant.routing_profile_id || '',
    });
    if (!ratePlans.length) api.get('/rates/plans?limit=200').then(r => setRatePlans(r.data?.data || r.data || [])).catch(() => {});
    if (!routingProfiles.length) api.get('/routing/profiles?limit=200').then(r => setRoutingProfiles(r.data?.data || r.data || [])).catch(() => {});
    setEditDialog(true);
  };
  const saveEdit = async () => {
    setEditErr('');
    try {
      const payload = { ...editForm, credit_limit: Number(editForm.credit_limit) || 0, rate_plan_id: editForm.rate_plan_id || null, routing_profile_id: editForm.routing_profile_id || null };
      await api.put(`/tenants/${id}`, payload);
      setEditDialog(false);
      loadTenant();
    } catch (e: any) { setEditErr(e.response?.data?.message || 'Save failed'); }
  };
  const reloadDids = () => api.get(`/numbers?tenant_id=${id}&limit=500`).then(r => setDids(r.data?.data || r.data || [])).catch(() => {});
  const saveDest = async (d: any) => {
    setDidMsg('');
    try {
      const val = (destEdits[d.id] ?? '').trim();
      await api.patch(`/numbers/${d.id}`, { inbound_target: val || null });
      setDestEdits(prev => { const n = { ...prev }; delete n[d.id]; return n; });
      setDidMsg(`Destination for ${d.number} updated → ${val || 'customer trunk default'}`);
      reloadDids();
    } catch (e: any) { setDidMsg(e.response?.data?.message || 'Save failed'); }
  };
  const toggleLive = async (row: any) => {
    setDidMsg('');
    try {
      if (row.is_live) {
        if (!window.confirm(`Take ${row.number} down? It will return 503 and route to the old switch.`)) return;
        const { data } = await api.post(`/numbers/${row.id}/take-down`); setDidMsg(data.message);
      } else {
        const { data } = await api.post(`/numbers/${row.id}/go-live`); setDidMsg(data.message);
      }
      reloadDids();
    } catch (e: any) { setDidMsg(e.response?.data?.message || 'Action failed'); }
  };

  if (loading) return <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress /></Box>;
  if (!tenant) return <Box sx={{ p: 4 }}><Button startIcon={<ArrowBack />} onClick={() => navigate('/tenants')}>Back</Button><Alert severity="error" sx={{ mt: 2 }}>Customer not found</Alert></Box>;

  const TABS = [
    { label: 'Overview', icon: <Business /> },
    { label: 'SIP Trunk', icon: <RouterIcon /> },
    { label: 'IP ACL', icon: <Security /> },
    { label: 'Caller ID', icon: <Badge /> },
    { label: 'DIDs', icon: <Dialpad /> },
    { label: 'Products', icon: <Inventory2 /> },
    { label: 'Invoices', icon: <ReceiptLong /> },
    { label: 'CDR', icon: <Call /> },
    { label: 'Billing', icon: <AccountBalance /> },
    { label: 'SIP Devices', icon: <PhoneAndroid /> },
    { label: 'KYC / Compliance', icon: <VerifiedUser /> },
    ...(me?.role === 'super_admin' ? [{ label: 'Direct Media', icon: <RouterIcon /> }] : []),
  ];
  const primaryDid = dids.find(d => d.features?.primary === 'true' || d.features?.primary === true);

  return (
    <Box>
      {/* Header */}
      <Button startIcon={<ArrowBack />} onClick={() => navigate('/tenants')} sx={{ mb: 1 }}>Customers</Button>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap" useFlexGap>
            <Avatar sx={{ bgcolor: 'primary.main', width: 52, height: 52, fontWeight: 700 }}>{(tenant.name || '?')[0]}</Avatar>
            <Box sx={{ flex: 1, minWidth: 200 }}>
              <Typography variant="h5" fontWeight={800}>{tenant.name}</Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 0.5 }} alignItems="center" flexWrap="wrap" useFlexGap>
                <Chip label={tenant.tier} size="small" color="info" variant="outlined" />
                <StatusChip s={tenant.status} />
                <Chip label={tenant.billing_type || 'postpaid'} size="small" variant="outlined" />
                {tenant.contact_email && <Typography variant="caption" color="text.secondary">{tenant.contact_email}</Typography>}
              </Stack>
            </Box>
            {!hideMoney && (
              <Stack direction="row" spacing={3}>
                <Box textAlign="right"><Typography variant="caption" color="text.secondary">Balance</Typography><Typography variant="h6" fontWeight={700}>{money(tenant.balance)}</Typography></Box>
                <Box textAlign="right"><Typography variant="caption" color="text.secondary">Credit Limit</Typography><Typography variant="h6" fontWeight={700}>{money(tenant.credit_limit)}</Typography></Box>
              </Stack>
            )}
            <Button variant="outlined" startIcon={<Edit />} onClick={openEdit}>Edit</Button>
          </Stack>
        </CardContent>
      </Card>

      <Card>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto" sx={{ borderBottom: 1, borderColor: 'divider' }}>
          {TABS.map((t, i) => <Tab key={i} icon={t.icon} iconPosition="start" label={t.label} sx={{ minHeight: 56 }} />)}
        </Tabs>
        <CardContent>
          {/* 0 Overview */}
          {tab === 0 && (
            <Grid container spacing={2}>
              {[['Customer', tenant.name], ['Code', tenant.code], ['Tier', tenant.tier], ['Status', tenant.status],
                ['Company', tenant.company_name], ['Email', tenant.contact_email], ['Phone', tenant.contact_phone],
                ['City', tenant.city], ['Currency', tenant.currency], ['Billing', tenant.billing_type],
                ['Rate Plan', tenant.rate_plan?.name || '—']].map(([k, v]) => (
                <Grid item xs={12} sm={6} md={4} key={k as string}>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>{k}</Typography>
                  <Typography variant="body1" fontWeight={600}>{(v as any) || '—'}</Typography>
                </Grid>
              ))}
            </Grid>
          )}

          {/* 1 SIP Trunk */}
          {tab === 1 && (
            <>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                <Alert severity="info" sx={{ flex: 1, mr: 2, py: 0.5 }}>The customer's SIP device / trunk — its ingress gateway(s). This is the peer that sends traffic to (and receives from) the SBC.</Alert>
                <Button size="small" variant="contained" startIcon={<Add />} onClick={() => { setTrunkErr(''); setTrunkDialog(true); }}>Add Trunk</Button>
              </Stack>
              <Table size="small"><TableHead><TableRow>{['Trunk', 'Host', 'Port', 'Transport', 'Auth', 'Primary', 'Status', ''].map(h => <TableCell key={h} sx={{ fontWeight: 700 }}>{h}</TableCell>)}</TableRow></TableHead>
                <TableBody>{trunk.map((r, i) => (
                  <TableRow key={i} hover>
                    <TableCell sx={{ fontWeight: 600 }}>{r.carrier_name}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{r.host || '—'}</TableCell>
                    <TableCell>{r.port || '—'}</TableCell>
                    <TableCell><Chip label={r.protocol || '—'} size="small" color={String(r.protocol).toUpperCase() === 'TLS' ? 'success' : 'default'} variant="outlined" /></TableCell>
                    <TableCell><Typography variant="caption">{r.auth_type === 'userpass' ? `user: ${r.username || '—'}` : 'IP'}</Typography></TableCell>
                    <TableCell>{r.is_primary ? <CheckCircle color="success" fontSize="small" /> : '—'}</TableCell>
                    <TableCell><StatusChip s={r.gateway_status || r.carrier_status} /></TableCell>
                    <TableCell><Tooltip title="Remove trunk"><IconButton size="small" color="error" onClick={() => delTrunk(r.carrier_id)}><Delete fontSize="small" /></IconButton></Tooltip></TableCell>
                  </TableRow>))}
                  {trunk.length === 0 && <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>No SIP trunk configured — click “Add Trunk” to define the customer's ingress gateway.</TableCell></TableRow>}
                </TableBody></Table>
            </>
          )}

          {/* 2 IP ACL */}
          {tab === 2 && (
            <>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                <Typography variant="body2" color="text.secondary">Trusted source IP addresses — traffic from these IPs is accepted as this customer.</Typography>
                <Button size="small" variant="contained" startIcon={<Add />} onClick={() => { setErr(''); setAclDialog(true); }}>Add IP</Button>
              </Stack>
              <Table size="small"><TableHead><TableRow>{['IP Address', 'Mask', 'Port', 'Label', 'Active', ''].map(h => <TableCell key={h} sx={{ fontWeight: 700 }}>{h}</TableCell>)}</TableRow></TableHead>
                <TableBody>{acl.map((r) => (
                  <TableRow key={r.id} hover>
                    <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{r.ip_addr}</TableCell>
                    <TableCell>/{r.mask}</TableCell>
                    <TableCell>{r.port === 0 ? 'any' : r.port}</TableCell>
                    <TableCell sx={{ color: 'text.secondary', fontSize: 12 }}>{r.tag}</TableCell>
                    <TableCell>{r.active ? <CheckCircle color="success" fontSize="small" /> : <Cancel color="disabled" fontSize="small" />}</TableCell>
                    <TableCell><Tooltip title="Remove"><IconButton size="small" color="error" onClick={() => delAcl(r.id)}><Delete fontSize="small" /></IconButton></Tooltip></TableCell>
                  </TableRow>))}
                  {acl.length === 0 && <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>No trusted IPs</TableCell></TableRow>}
                </TableBody></Table>
            </>
          )}

          {/* 3 Caller ID */}
          {tab === 3 && (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                The customer sends its own <b>presentation CLI (From)</b>, which passes through to the carrier as-is.
                The carrier (BT) authenticates on <b>P-Asserted-Identity</b>, which must be one of the customer's
                allocated DIDs — configured below.
              </Alert>

              <Card variant="outlined" sx={{ mb: 2, p: 2 }}>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>P-Asserted-Identity source</Typography>
                <RadioGroup value={cli.pai_policy} onChange={e => { setCli({ ...cli, pai_policy: e.target.value }); setCliMsg(''); }}>
                  <FormControlLabel value="pool" control={<Radio />} label={
                    <Box sx={{ py: 0.5 }}>
                      <Typography variant="body2" fontWeight={600}>Use the allocated DID pool (recommended)</Typography>
                      <Typography variant="caption" color="text.secondary">If the From/CLI <b>matches</b> one of this customer's DIDs → present that same number as PAI. If it <b>doesn't match</b> → present a random DID from the assigned pool.</Typography>
                    </Box>} />
                  <FormControlLabel value="fixed" control={<Radio />} label={
                    <Box sx={{ py: 0.5 }}>
                      <Typography variant="body2" fontWeight={600}>Always use one specific DID</Typography>
                      <Typography variant="caption" color="text.secondary">Every call presents this single DID as PAI, regardless of the From/CLI.</Typography>
                    </Box>} />
                </RadioGroup>
                {cli.pai_policy === 'fixed' && (
                  <FormControl size="small" sx={{ mt: 1, minWidth: 280 }}>
                    <InputLabel>Fixed PAI DID *</InputLabel>
                    <Select label="Fixed PAI DID *" value={cli.pai_fixed_did} onChange={e => setCli({ ...cli, pai_fixed_did: e.target.value })}>
                      {dids.map(d => <MenuItem key={d.id} value={d.number}>{d.number}</MenuItem>)}
                    </Select>
                  </FormControl>
                )}
                <Box sx={{ mt: 1.5, display: 'flex', gap: 1.5, alignItems: 'center' }}>
                  <Button variant="contained" size="small" onClick={saveCli} disabled={cli.pai_policy === 'fixed' && !cli.pai_fixed_did}>Save policy</Button>
                  {cliMsg && <Typography variant="caption" color={cliMsg.includes('✓') ? 'success.main' : 'error.main'} fontWeight={600}>{cliMsg}</Typography>}
                </Box>
              </Card>

              <Card variant="outlined" sx={{ mb: 2, p: 2 }}>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>Presentation number (From) overrides by destination</Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                  Normally the customer's own From/CLI passes through unchanged. For the destination prefixes listed here,
                  the SBC replaces the From with the <b>same allocated BT number it asserts as P-Asserted-Identity</b>
                  (useful where the carrier declines unverified foreign caller IDs on international routes).
                  Changes apply to the next call — no restart. Other destinations and other customers are unaffected.
                </Typography>
                <Table size="small"><TableHead><TableRow>{['Destination prefix', 'Behaviour', 'Description', 'Enabled', ''].map(h => <TableCell key={h} sx={{ fontWeight: 700 }}>{h}</TableCell>)}</TableRow></TableHead>
                  <TableBody>{overrides.map((o: any) => (
                    <TableRow key={o.id} hover sx={{ opacity: o.active ? 1 : 0.55 }}>
                      <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{o.dst_prefix}</TableCell>
                      <TableCell><Chip size="small" label="From = asserted BT DID" variant="outlined" /></TableCell>
                      <TableCell sx={{ color: 'text.secondary', fontSize: 12 }}>{o.description || '—'}</TableCell>
                      <TableCell><Switch size="small" checked={!!o.active} onChange={() => toggleOverride(o)} /></TableCell>
                      <TableCell align="right"><Tooltip title="Remove override"><IconButton size="small" color="error" onClick={() => removeOverride(o)}><Delete fontSize="small" /></IconButton></Tooltip></TableCell>
                    </TableRow>))}
                    {overrides.length === 0 && <TableRow><TableCell colSpan={5} align="center" sx={{ py: 2, color: 'text.secondary' }}>No overrides — the customer's From passes through as-is on every destination</TableCell></TableRow>}
                  </TableBody></Table>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.5 }}>
                  <TextField size="small" label="Destination prefix" placeholder="e.g. 65 or 852" value={ovNew.dst_prefix}
                    onChange={e => setOvNew({ ...ovNew, dst_prefix: e.target.value.replace(/[^0-9]/g, '') })} sx={{ width: 170 }} />
                  <TextField size="small" label="Description (optional)" value={ovNew.description}
                    onChange={e => setOvNew({ ...ovNew, description: e.target.value })} sx={{ flex: 1, maxWidth: 420 }} />
                  <Button variant="contained" size="small" startIcon={<Add />} onClick={addOverride} disabled={!ovNew.dst_prefix}>Add override</Button>
                  {ovMsg && <Typography variant="caption" color={ovMsg.includes('✓') ? 'success.main' : 'error.main'} fontWeight={600}>{ovMsg}</Typography>}
                </Stack>
              </Card>

              <Typography variant="subtitle2" sx={{ mb: 1 }}>Allocated numbers used for Caller-ID {primaryDid && <>· default: <b>{primaryDid.number}</b></>}</Typography>
              <Table size="small"><TableHead><TableRow>{['Number', 'Default CLI', 'Carrier', 'Live'].map(h => <TableCell key={h} sx={{ fontWeight: 700 }}>{h}</TableCell>)}</TableRow></TableHead>
                <TableBody>{dids.map((d) => (
                  <TableRow key={d.id} hover>
                    <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{d.number}</TableCell>
                    <TableCell>{(d.features?.primary === 'true' || d.features?.primary === true) ? <Chip label="DEFAULT" size="small" color="primary" /> : '—'}</TableCell>
                    <TableCell sx={{ color: 'text.secondary' }}>{d.carrier_name || d.carrier_id || '—'}</TableCell>
                    <TableCell>{d.is_live ? <Chip label="LIVE" size="small" color="success" /> : <Chip label="—" size="small" variant="outlined" />}</TableCell>
                  </TableRow>))}
                  {dids.length === 0 && <TableRow><TableCell colSpan={4} align="center" sx={{ py: 4, color: 'text.secondary' }}>No DIDs allocated</TableCell></TableRow>}
                </TableBody></Table>
            </>
          )}

          {/* 4 DIDs */}
          {tab === 4 && (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                Flip <b>Live on esbc2</b> to migrate a DID one at a time. <b>Off</b> = esbc2 returns 503 so the carrier
                routes it to the old switch; <b>On</b> = handled here and delivered to the <b>Destination</b> below.
                A DID's destination defaults to the customer's SIP-trunk IP on assignment — edit it to override.
              </Alert>
              {didMsg && <Alert severity={didMsg.toLowerCase().includes('updated') || didMsg.toLowerCase().includes('live') || didMsg.toLowerCase().includes('down') ? 'success' : 'error'} sx={{ mb: 2 }} onClose={() => setDidMsg('')}>{didMsg}</Alert>}
              <Table size="small"><TableHead><TableRow>{['DID Number', 'Type', 'Status', 'Destination', 'Live on esbc2'].map(h => <TableCell key={h} sx={{ fontWeight: 700 }}>{h}</TableCell>)}</TableRow></TableHead>
                <TableBody>{dids.map((d) => {
                  const dest = destEdits[d.id] ?? d.inbound_target ?? '';
                  const dirty = destEdits[d.id] !== undefined && destEdits[d.id] !== (d.inbound_target ?? '');
                  return (
                  <TableRow key={d.id} hover>
                    <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{d.number}</TableCell>
                    <TableCell><Chip label={d.number_type} size="small" variant="outlined" /></TableCell>
                    <TableCell><Chip label={d.status} size="small" color={d.status === 'assigned' ? 'primary' : 'default'} /></TableCell>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <TextField size="small" variant="standard" placeholder="customer trunk (default)"
                          value={dest}
                          onChange={e => setDestEdits({ ...destEdits, [d.id]: e.target.value })}
                          onKeyDown={e => { if (e.key === 'Enter' && dirty) saveDest(d); }}
                          sx={{ minWidth: 210 }}
                          InputProps={{ sx: { fontFamily: 'monospace', fontSize: 12 } }} />
                        <Tooltip title={dirty ? 'Save destination' : 'Edit then save'}>
                          <span><IconButton size="small" color="primary" disabled={!dirty} onClick={() => saveDest(d)}><CheckCircle fontSize="small" /></IconButton></span>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <Switch size="small" color="success" checked={!!d.is_live}
                          disabled={d.status !== 'assigned'}
                          onChange={() => toggleLive(d)} />
                        {d.is_live
                          ? <Chip label="LIVE" size="small" color="success" sx={{ height: 20 }} />
                          : <Chip label="503 → old" size="small" variant="outlined" sx={{ height: 20 }} />}
                      </Stack>
                    </TableCell>
                  </TableRow>); })}
                  {dids.length === 0 && <TableRow><TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>No DIDs assigned</TableCell></TableRow>}
                </TableBody></Table>
            </>
          )}

          {/* 5 Products */}
          {tab === 5 && (
            <Table size="small"><TableHead><TableRow>{['Product / SKU', 'Qty', 'Price', 'Status', 'Start'].map(h => <TableCell key={h} sx={{ fontWeight: 700 }}>{h}</TableCell>)}</TableRow></TableHead>
              <TableBody>{products.map((p, i) => (
                <TableRow key={i} hover>
                  <TableCell sx={{ fontWeight: 600 }}>{p.sku_name || p.product_name || p.sku_id || '—'}</TableCell>
                  <TableCell>{p.quantity ?? 1}</TableCell>
                  <TableCell>{hideMoney ? '—' : money(p.price)}</TableCell>
                  <TableCell><Chip label={p.status} size="small" color={p.status === 'active' ? 'success' : 'default'} /></TableCell>
                  <TableCell sx={{ color: 'text.secondary' }}>{(p.start_date || '').slice(0, 10) || '—'}</TableCell>
                </TableRow>))}
                {products.length === 0 && <TableRow><TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>No products / subscriptions</TableCell></TableRow>}
              </TableBody></Table>
          )}

          {/* 6 Invoices */}
          {tab === 6 && (
            <Table size="small"><TableHead><TableRow>{['Invoice #', 'Period', 'Amount', 'Status', 'Due'].map(h => <TableCell key={h} sx={{ fontWeight: 700 }}>{h}</TableCell>)}</TableRow></TableHead>
              <TableBody>{invoices.map((v, i) => (
                <TableRow key={i} hover>
                  <TableCell sx={{ fontFamily: 'monospace' }}>{v.invoice_number || v.number || v.id?.slice(0, 8)}</TableCell>
                  <TableCell sx={{ color: 'text.secondary' }}>{(v.period_start || '').slice(0, 10)} → {(v.period_end || '').slice(0, 10)}</TableCell>
                  <TableCell>{hideMoney ? '—' : money(v.total ?? v.amount)}</TableCell>
                  <TableCell><Chip label={v.status} size="small" color={v.status === 'paid' ? 'success' : v.status === 'overdue' ? 'error' : 'warning'} /></TableCell>
                  <TableCell sx={{ color: 'text.secondary' }}>{(v.due_date || '').slice(0, 10) || '—'}</TableCell>
                </TableRow>))}
                {invoices.length === 0 && <TableRow><TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>No invoices</TableCell></TableRow>}
              </TableBody></Table>
          )}

          {/* 7 CDR */}
          {tab === 7 && (
            <Table size="small"><TableHead><TableRow>{['Time', 'Dir', 'From', 'To', 'Dur', 'Status', 'Cost', 'Trace'].map(h => <TableCell key={h} sx={{ fontWeight: 700 }}>{h}</TableCell>)}</TableRow></TableHead>
              <TableBody>{cdrs.map((c, i) => (
                <TableRow key={i} hover>
                  <TableCell sx={{ color: 'text.secondary', fontSize: 12 }}>{(c.start_time || '').replace('T', ' ').slice(0, 19)}</TableCell>
                  <TableCell><Chip label={c.direction === 'inbound' ? 'IN' : 'OUT'} size="small" color={c.direction === 'inbound' ? 'info' : 'secondary'} variant="outlined" /></TableCell>
                  <TableCell sx={{ fontFamily: 'monospace' }}>{c.src_number}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace' }}>{c.dst_number}</TableCell>
                  <TableCell>{c.billsec ?? c.duration ?? 0}s</TableCell>
                  <TableCell>
                    <Tooltip title={c.hangup_reason ? `${c.hangup_reason}${c.hangup_code ? ' · SIP ' + c.hangup_code : ''}` : ''}>
                      <Chip label={c.status} size="small" variant="outlined" color={c.status === 'answered' ? 'success' : c.status === 'busy' ? 'warning' : 'error'} />
                    </Tooltip>
                    {c.status !== 'answered' && c.hangup_reason && <Typography variant="caption" display="block" sx={{ color: 'text.secondary', mt: 0.3 }}>{c.hangup_reason}</Typography>}
                  </TableCell>
                  <TableCell>{hideMoney ? '—' : money(c.cost ?? c.sell_cost)}</TableCell>
                  <TableCell>
                    <Tooltip title="SIP ladder"><span><IconButton size="small" onClick={() => { setTraceId(c.call_id || c.sip_call_id); setTraceOpen(true); }} disabled={!(c.call_id || c.sip_call_id)}><BugReport fontSize="small" sx={{ color: '#00b0ff' }} /></IconButton></span></Tooltip>
                  </TableCell>
                </TableRow>))}
                {cdrs.length === 0 && <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>No call records</TableCell></TableRow>}
              </TableBody></Table>
          )}

          {/* 8 Billing */}
          {tab === 8 && (
            <>
              {hideMoney ? <Alert severity="info">Financial figures are hidden for your role.</Alert> : (
                <>
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={6} md={3}><Card variant="outlined"><CardContent><Typography variant="caption" color="text.secondary">BALANCE</Typography><Typography variant="h5" fontWeight={700}>{money(balance?.balance ?? tenant.balance)}</Typography></CardContent></Card></Grid>
                    <Grid item xs={6} md={3}><Card variant="outlined"><CardContent><Typography variant="caption" color="text.secondary">CREDIT LIMIT</Typography><Typography variant="h5" fontWeight={700}>{money(balance?.credit_limit ?? tenant.credit_limit)}</Typography></CardContent></Card></Grid>
                    <Grid item xs={6} md={3}><Card variant="outlined"><CardContent><Typography variant="caption" color="text.secondary">PENDING BILLS</Typography><Typography variant="h5" fontWeight={700} color="warning.main">{money(invoices.filter(v => v.status !== 'paid').reduce((s, v) => s + Number(v.total ?? v.amount ?? 0), 0))}</Typography></CardContent></Card></Grid>
                    <Grid item xs={6} md={3}><Card variant="outlined"><CardContent><Typography variant="caption" color="text.secondary">CURRENCY</Typography><Typography variant="h5" fontWeight={700}>{tenant.currency || 'USD'}</Typography></CardContent></Card></Grid>
                  </Grid>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Recent transactions</Typography>
                  <Table size="small"><TableHead><TableRow>{['Date', 'Type', 'Amount', 'Description'].map(h => <TableCell key={h} sx={{ fontWeight: 700 }}>{h}</TableCell>)}</TableRow></TableHead>
                    <TableBody>{txns.slice(0, 20).map((t, i) => (
                      <TableRow key={i} hover>
                        <TableCell sx={{ color: 'text.secondary', fontSize: 12 }}>{(t.created_at || '').slice(0, 19).replace('T', ' ')}</TableCell>
                        <TableCell><Chip label={t.type || t.txn_type} size="small" variant="outlined" /></TableCell>
                        <TableCell sx={{ fontWeight: 600, color: (t.type === 'credit' || Number(t.amount) > 0) ? 'success.main' : 'error.main' }}>{money(t.amount)}</TableCell>
                        <TableCell sx={{ color: 'text.secondary' }}>{t.description || '—'}</TableCell>
                      </TableRow>))}
                      {txns.length === 0 && <TableRow><TableCell colSpan={4} align="center" sx={{ py: 4, color: 'text.secondary' }}>No transactions</TableCell></TableRow>}
                    </TableBody></Table>
                </>
              )}
            </>
          )}

          {/* 9 SIP Devices */}
          {tab === 9 && (
            <>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                <Alert severity="info" sx={{ flex: 1, mr: 2, py: 0.5 }}>Registered SIP accounts — the customer's softphone/PBX authenticates with these credentials over <b>TLS&nbsp;port&nbsp;5080</b> (no IP needed). DIDs can be delivered to a registered device.</Alert>
                <Button size="small" variant="contained" startIcon={<Add />} onClick={() => { setDevErr(''); setNewDev({ caller_number: '', voicemail: false }); setDevDialog(true); }}>Add Device</Button>
              </Stack>
              <Table size="small"><TableHead><TableRow>{['Username', 'Password', 'Caller Number', 'Voicemail', 'Registered', 'Status', ''].map(h => <TableCell key={h} sx={{ fontWeight: 700 }}>{h}</TableCell>)}</TableRow></TableHead>
                <TableBody>{devices.map((d) => (
                  <TableRow key={d.id} hover>
                    <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{d.registered ? <CheckCircle color="success" fontSize="inherit" sx={{ mr: 0.5, verticalAlign: 'middle' }} /> : null}{d.username}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace' }}>
                      {showPw[d.id] ? d.password : '••••••••'}
                      <Tooltip title={showPw[d.id] ? 'Hide' : 'Show'}><IconButton size="small" onClick={() => setShowPw({ ...showPw, [d.id]: !showPw[d.id] })}><VpnKey fontSize="inherit" /></IconButton></Tooltip>
                    </TableCell>
                    <TableCell>{d.caller_number || '—'}</TableCell>
                    <TableCell>{d.voicemail ? <CheckCircle color="success" fontSize="small" /> : '—'}</TableCell>
                    <TableCell>{d.registered ? <Chip label="ONLINE" size="small" color="success" /> : <Chip label="offline" size="small" variant="outlined" />}</TableCell>
                    <TableCell><Switch size="small" checked={d.status === 'active'} onChange={() => toggleDevice(d)} /></TableCell>
                    <TableCell><Tooltip title="Delete device"><IconButton size="small" color="error" onClick={() => delDevice(d.id)}><Delete fontSize="small" /></IconButton></Tooltip></TableCell>
                  </TableRow>))}
                  {devices.length === 0 && <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>No SIP devices — click “Add Device” to issue registration credentials.</TableCell></TableRow>}
                </TableBody></Table>
            </>
          )}

          {/* 11 Direct Media (RTP bypass) — super-admin only */}
          {tab === 11 && id && <DirectMediaTab tenantId={id} />}

          {/* 10 KYC / Compliance */}
          {tab === KYC_TAB && (
            !kyc ? <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box> : (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Alert severity={kyc.profile?.kyc_status === 'approved' ? 'success' : kyc.profile?.kyc_status === 'rejected' ? 'error' : kyc.profile?.kyc_status === 'in_review' ? 'warning' : 'info'}>
                  KYC status: <b>{(kyc.profile?.kyc_status || 'not_started').replace('_', ' ')}</b> — {kyc.summary.approved}/{kyc.summary.total} documents approved
                  {kyc.summary.pending > 0 && `, ${kyc.summary.pending} pending review`}
                  {kyc.summary.expired > 0 && `, ${kyc.summary.expired} expired`}. A <b>Number authorization / LOA</b> is what proves this customer may present the CLIs they send.
                </Alert>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>Business & regulatory profile</Typography>
                <Stack spacing={1.5}>
                  <TextField size="small" label="Legal entity name" value={kycForm.legal_entity_name || ''} onChange={e => setKycForm({ ...kycForm, legal_entity_name: e.target.value })} />
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                    <TextField size="small" label="Registration number" value={kycForm.registration_number || ''} onChange={e => setKycForm({ ...kycForm, registration_number: e.target.value })} />
                    <TextField size="small" label="Country of incorporation" value={kycForm.incorporation_country || ''} onChange={e => setKycForm({ ...kycForm, incorporation_country: e.target.value })} />
                    <TextField size="small" label="Tax ID" value={kycForm.tax_id || ''} onChange={e => setKycForm({ ...kycForm, tax_id: e.target.value })} />
                    <TextField size="small" label="Origination countries" value={kycForm.origination_countries || ''} onChange={e => setKycForm({ ...kycForm, origination_countries: e.target.value })} placeholder="e.g. PK, AE" />
                    <TextField size="small" label="OCN (US carrier)" value={kycForm.ocn || ''} onChange={e => setKycForm({ ...kycForm, ocn: e.target.value })} />
                    <TextField size="small" label="FCC 499 Filer ID" value={kycForm.fcc_499_id || ''} onChange={e => setKycForm({ ...kycForm, fcc_499_id: e.target.value })} />
                  </Box>
                  <FormControlLabel control={<Switch checked={!!kycForm.rmd_registered} onChange={e => setKycForm({ ...kycForm, rmd_registered: e.target.checked })} />} label="Registered in FCC Robocall Mitigation Database (RMD)" />
                </Stack>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>Traceback contact & attestation</Typography>
                <Stack spacing={1.5}>
                  <TextField size="small" label="24h traceback contact name" value={kycForm.traceback_contact_name || ''} onChange={e => setKycForm({ ...kycForm, traceback_contact_name: e.target.value })} />
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                    <TextField size="small" label="Traceback email" value={kycForm.traceback_contact_email || ''} onChange={e => setKycForm({ ...kycForm, traceback_contact_email: e.target.value })} />
                    <TextField size="small" label="Traceback phone" value={kycForm.traceback_contact_phone || ''} onChange={e => setKycForm({ ...kycForm, traceback_contact_phone: e.target.value })} />
                  </Box>
                  <FormControlLabel control={<Switch checked={!!kycForm.no_spoofing_attested} onChange={e => setKycForm({ ...kycForm, no_spoofing_attested: e.target.checked })} />} label="Customer attested: authorized to use presented numbers (no spoofing)" />
                  {kyc.profile?.attested_at && <Typography variant="caption" color="text.secondary">Attested {new Date(kyc.profile.attested_at).toLocaleDateString()}</Typography>}
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                    <FormControl size="small"><InputLabel>KYC status</InputLabel>
                      <Select label="KYC status" value={kycForm.kyc_status || 'not_started'} onChange={e => setKycForm({ ...kycForm, kyc_status: e.target.value })}>
                        {['not_started', 'in_review', 'approved', 'rejected'].map(s => <MenuItem key={s} value={s}>{s.replace('_', ' ')}</MenuItem>)}
                      </Select>
                    </FormControl>
                    <TextField size="small" type="date" label="KYC expiry" InputLabelProps={{ shrink: true }} value={kycForm.kyc_expiry || ''} onChange={e => setKycForm({ ...kycForm, kyc_expiry: e.target.value })} />
                  </Box>
                  <TextField size="small" label="Notes" multiline minRows={2} value={kycForm.notes || ''} onChange={e => setKycForm({ ...kycForm, notes: e.target.value })} />
                </Stack>
              </Grid>

              <Grid item xs={12}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Button variant="contained" onClick={saveKycProfile}>Save profile</Button>
                  {kycMsg && <Typography variant="body2" color={kycMsg.includes('✓') ? 'success.main' : 'error.main'}>{kycMsg}</Typography>}
                </Stack>
              </Grid>

              <Grid item xs={12}><Divider /></Grid>

              <Grid item xs={12}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                  <Typography variant="subtitle2" fontWeight={700}>Documents ({kyc.summary.total})</Typography>
                  <Button size="small" variant="contained" startIcon={<UploadFile />} onClick={() => { setKycUpErr(''); setKycFile(null); setKycUp({ doc_type: 'business_registration', title: '', issued_date: '', expiry_date: '' }); setKycUpOpen(true); }}>Upload document</Button>
                </Stack>
                <Table size="small">
                  <TableHead><TableRow>{['Type', 'File', 'Status', 'Issued', 'Expires', 'Reviewed', ''].map(h => <TableCell key={h} sx={{ fontWeight: 700 }}>{h}</TableCell>)}</TableRow></TableHead>
                  <TableBody>
                    {kyc.documents.map((d: any) => (
                      <TableRow key={d.id} hover>
                        <TableCell>{DOC_TYPE_LABELS[d.doc_type] || d.doc_type}{d.title && <Typography variant="caption" display="block" color="text.secondary">{d.title}</Typography>}</TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{d.file_name}</TableCell>
                        <TableCell><Chip size="small" label={d.status} color={DOC_STATUS_COLOR[d.status] || 'default'} /></TableCell>
                        <TableCell>{d.issued_date ? String(d.issued_date).slice(0, 10) : '—'}</TableCell>
                        <TableCell>{d.expiry_date ? String(d.expiry_date).slice(0, 10) : '—'}</TableCell>
                        <TableCell>{d.reviewed_at ? new Date(d.reviewed_at).toLocaleDateString() : '—'}</TableCell>
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                          <Tooltip title="Download"><IconButton size="small" onClick={() => downloadKycDoc(d)}><Download fontSize="small" /></IconButton></Tooltip>
                          <Tooltip title="Approve"><IconButton size="small" color="success" onClick={() => openKycReview(d, 'approved')}><CheckCircle fontSize="small" /></IconButton></Tooltip>
                          <Tooltip title="Reject"><IconButton size="small" color="warning" onClick={() => openKycReview(d, 'rejected')}><Cancel fontSize="small" /></IconButton></Tooltip>
                          <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => deleteKycDoc(d)}><Delete fontSize="small" /></IconButton></Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                    {kyc.documents.length === 0 && <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>No documents uploaded yet.</TableCell></TableRow>}
                  </TableBody>
                </Table>
              </Grid>
            </Grid>
            )
          )}
        </CardContent>
      </Card>

      {/* Add IP dialog */}
      <Dialog open={aclDialog} onClose={() => setAclDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add trusted IP for {tenant.name}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}
          <TextField fullWidth label="IP Address *" value={newIp.ip_addr} onChange={e => setNewIp({ ...newIp, ip_addr: e.target.value })} placeholder="e.g. 151.253.188.4" sx={{ mb: 2 }} />
          <TextField fullWidth type="number" label="Mask" value={newIp.mask} onChange={e => setNewIp({ ...newIp, mask: parseInt(e.target.value) || 32 })} sx={{ mb: 2 }} />
          <TextField fullWidth label="Description" value={newIp.description} onChange={e => setNewIp({ ...newIp, description: e.target.value })} />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setAclDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={addAcl} disabled={!newIp.ip_addr}>Add IP</Button>
        </DialogActions>
      </Dialog>

      {/* Add customer trunk dialog */}
      <Dialog open={trunkDialog} onClose={() => setTrunkDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add SIP trunk for {tenant.name}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {trunkErr && <Alert severity="error" sx={{ mb: 2 }}>{trunkErr}</Alert>}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mt: 1 }}>
            <Box sx={{ gridColumn: { sm: '1 / -1' } }}><TextField fullWidth label="Trunk name" value={newTrunk.name} onChange={e => setNewTrunk({ ...newTrunk, name: e.target.value })} placeholder={`${tenant.name} (ingress)`} helperText="Leave blank to auto-name" /></Box>
            <Box sx={{ gridColumn: { sm: '1 / -1' }, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '2fr 1fr' }, gap: 2 }}>
              <TextField fullWidth label="Host / IP *" value={newTrunk.host} onChange={e => setNewTrunk({ ...newTrunk, host: e.target.value })} placeholder="e.g. 151.253.188.4" />
              <TextField fullWidth type="number" label="Port" value={newTrunk.port} onChange={e => setNewTrunk({ ...newTrunk, port: parseInt(e.target.value) || 5060 })} />
            </Box>
            <FormControl fullWidth><InputLabel>Transport</InputLabel>
              <Select value={newTrunk.protocol} label="Transport" onChange={e => setNewTrunk({ ...newTrunk, protocol: e.target.value })}>
                <MenuItem value="UDP">UDP</MenuItem><MenuItem value="TCP">TCP</MenuItem><MenuItem value="TLS">TLS</MenuItem>
              </Select></FormControl>
            <FormControl fullWidth><InputLabel>Authentication</InputLabel>
              <Select value={newTrunk.auth_type} label="Authentication" onChange={e => setNewTrunk({ ...newTrunk, auth_type: e.target.value })}>
                <MenuItem value="ip">IP-based</MenuItem><MenuItem value="userpass">Username / Password</MenuItem>
              </Select></FormControl>
            {newTrunk.auth_type === 'userpass' && <>
              <TextField fullWidth label="Username" value={newTrunk.username} onChange={e => setNewTrunk({ ...newTrunk, username: e.target.value })} />
              <TextField fullWidth label="Password" type="password" value={newTrunk.password} onChange={e => setNewTrunk({ ...newTrunk, password: e.target.value })} />
            </>}
            <FormControl fullWidth><InputLabel>Media (SRTP)</InputLabel>
              <Select value={newTrunk.srtp} label="Media (SRTP)" onChange={e => setNewTrunk({ ...newTrunk, srtp: e.target.value })}>
                <MenuItem value="none">Plain RTP</MenuItem><MenuItem value="optional">SRTP optional</MenuItem><MenuItem value="require">SRTP required</MenuItem>
              </Select></FormControl>
          </Box>
          <Alert severity="info" sx={{ mt: 2 }}>An IP-based trunk auto-adds its host to this customer's trusted IPs, Kamailio permissions, and the SIP firewall allow-list.</Alert>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setTrunkDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={addTrunk} disabled={!newTrunk.host}>Create Trunk</Button>
        </DialogActions>
      </Dialog>

      {/* Edit customer dialog */}
      <Dialog open={editDialog} onClose={() => setEditDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit {tenant.name}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {editErr && <Alert severity="error" sx={{ mb: 2 }}>{editErr}</Alert>}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mt: 1 }}>
            <TextField fullWidth label="Name" value={editForm.name || ''} onChange={e => setEditForm({ ...editForm, name: e.target.value })} />
            <TextField fullWidth label="Company" value={editForm.company_name || ''} onChange={e => setEditForm({ ...editForm, company_name: e.target.value })} />
            <TextField fullWidth label="Contact email" value={editForm.contact_email || ''} onChange={e => setEditForm({ ...editForm, contact_email: e.target.value })} />
            <TextField fullWidth label="Contact phone" value={editForm.contact_phone || ''} onChange={e => setEditForm({ ...editForm, contact_phone: e.target.value })} />
            <TextField fullWidth label="City" value={editForm.city || ''} onChange={e => setEditForm({ ...editForm, city: e.target.value })} />
            <FormControl fullWidth><InputLabel>Status</InputLabel>
              <Select value={editForm.status || 'active'} label="Status" onChange={e => setEditForm({ ...editForm, status: e.target.value })}>
                <MenuItem value="active">Active</MenuItem><MenuItem value="suspended">Suspended</MenuItem><MenuItem value="inactive">Inactive</MenuItem>
              </Select></FormControl>
            {!hideMoney && (
              <Box sx={{ gridColumn: { sm: '1 / -1' }, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 2 }}>
                <FormControl fullWidth><InputLabel>Billing type</InputLabel>
                  <Select value={editForm.billing_type || 'postpaid'} label="Billing type" onChange={e => setEditForm({ ...editForm, billing_type: e.target.value })}>
                    <MenuItem value="postpaid">Postpaid</MenuItem><MenuItem value="prepaid">Prepaid</MenuItem>
                  </Select></FormControl>
                <TextField fullWidth label="Currency" value={editForm.currency || ''} onChange={e => setEditForm({ ...editForm, currency: e.target.value })} />
                <TextField fullWidth type="number" label="Credit limit" value={editForm.credit_limit ?? 0} onChange={e => setEditForm({ ...editForm, credit_limit: e.target.value })} />
              </Box>
            )}
            <FormControl fullWidth><InputLabel>Rate plan</InputLabel>
              <Select value={editForm.rate_plan_id || ''} label="Rate plan" onChange={e => setEditForm({ ...editForm, rate_plan_id: e.target.value })}>
                <MenuItem value=""><em>— None —</em></MenuItem>
                {ratePlans.map(p => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
              </Select></FormControl>
            <FormControl fullWidth><InputLabel>Routing profile</InputLabel>
              <Select value={editForm.routing_profile_id || ''} label="Routing profile" onChange={e => setEditForm({ ...editForm, routing_profile_id: e.target.value })}>
                <MenuItem value=""><em>— None —</em></MenuItem>
                {routingProfiles.map(p => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
              </Select></FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setEditDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveEdit} disabled={!editForm.name}>Save changes</Button>
        </DialogActions>
      </Dialog>

      {/* Add SIP device */}
      <Dialog open={devDialog} onClose={() => setDevDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>New SIP device for {tenant.name}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {devErr && <Alert severity="error" sx={{ mb: 2 }}>{devErr}</Alert>}
          <Alert severity="info" sx={{ mb: 2 }}>A username &amp; strong password are auto-generated. The device registers to <b>{`esbc2.synergytalk.org:5080 (TLS)`}</b>.</Alert>
          <TextField fullWidth label="Caller Number (outbound CLI)" value={newDev.caller_number} onChange={e => setNewDev({ ...newDev, caller_number: e.target.value })} placeholder="e.g. +442046131900" sx={{ mb: 2 }} />
          <FormControlLabel control={<Switch checked={!!newDev.voicemail} onChange={e => setNewDev({ ...newDev, voicemail: e.target.checked })} />} label="Voicemail enabled" />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDevDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={addDevice}>Create Device</Button>
        </DialogActions>
      </Dialog>

      {/* Show generated credentials once */}
      <Dialog open={!!devCreds} onClose={() => setDevCreds(null)} maxWidth="xs" fullWidth>
        <DialogTitle>SIP device created</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Alert severity="success" sx={{ mb: 2 }}>Provision the customer's device with these credentials. The password is shown now — it's also visible later via the eye icon.</Alert>
          <Stack spacing={1.2} sx={{ fontFamily: 'monospace', fontSize: 14 }}>
            <Box><b>Username:</b> {devCreds?.username}</Box>
            <Box><b>Password:</b> {devCreds?.password}</Box>
            <Box><b>Domain / Realm:</b> {devCreds?.domain}</Box>
            <Box><b>Proxy / Registrar:</b> esbc2.synergytalk.org</Box>
            <Box><b>Transport / Port:</b> TLS 5080</Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button variant="contained" onClick={() => setDevCreds(null)}>Done</Button>
        </DialogActions>
      </Dialog>

      {/* KYC: upload document */}
      <Dialog open={kycUpOpen} onClose={() => setKycUpOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Upload KYC document — {tenant.name}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {kycUpErr && <Alert severity="error" sx={{ mb: 2 }}>{kycUpErr}</Alert>}
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl fullWidth><InputLabel>Document type *</InputLabel>
              <Select label="Document type *" value={kycUp.doc_type} onChange={e => setKycUp({ ...kycUp, doc_type: e.target.value })}>
                {Object.entries(DOC_TYPE_LABELS).map(([v, l]) => <MenuItem key={v} value={v}>{l}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField fullWidth label="Title / reference (optional)" value={kycUp.title} onChange={e => setKycUp({ ...kycUp, title: e.target.value })} />
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <TextField fullWidth type="date" label="Issued date" InputLabelProps={{ shrink: true }} value={kycUp.issued_date} onChange={e => setKycUp({ ...kycUp, issued_date: e.target.value })} />
              <TextField fullWidth type="date" label="Expiry date" InputLabelProps={{ shrink: true }} value={kycUp.expiry_date} onChange={e => setKycUp({ ...kycUp, expiry_date: e.target.value })} />
            </Box>
            <Button variant="outlined" component="label" startIcon={<UploadFile />}>
              {kycFile ? kycFile.name : 'Choose file (PDF, image, doc — max 25 MB)'}
              <input hidden type="file" accept=".pdf,.png,.jpg,.jpeg,.gif,.webp,.tiff,.doc,.docx,.xls,.xlsx,.txt" onChange={e => setKycFile(e.target.files?.[0] || null)} />
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setKycUpOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={doUploadKyc} disabled={!kycFile}>Upload</Button>
        </DialogActions>
      </Dialog>

      {/* KYC: review document */}
      <Dialog open={!!kycReviewDoc} onClose={() => setKycReviewDoc(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Review document</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {kycReviewDoc && <Typography variant="body2" sx={{ mb: 2 }} color="text.secondary">{DOC_TYPE_LABELS[kycReviewDoc.doc_type] || kycReviewDoc.doc_type} — <span style={{ fontFamily: 'monospace' }}>{kycReviewDoc.file_name}</span></Typography>}
          <Stack spacing={2}>
            <FormControl fullWidth><InputLabel>Decision</InputLabel>
              <Select label="Decision" value={kycReview.status} onChange={e => setKycReview({ ...kycReview, status: e.target.value })}>
                <MenuItem value="approved">Approve</MenuItem>
                <MenuItem value="rejected">Reject</MenuItem>
                <MenuItem value="pending">Reset to pending</MenuItem>
              </Select>
            </FormControl>
            <TextField fullWidth type="date" label="Expiry date (optional)" InputLabelProps={{ shrink: true }} value={kycReview.expiry_date} onChange={e => setKycReview({ ...kycReview, expiry_date: e.target.value })} />
            <TextField fullWidth label="Review notes" multiline minRows={2} value={kycReview.review_notes} onChange={e => setKycReview({ ...kycReview, review_notes: e.target.value })} />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setKycReviewDoc(null)}>Cancel</Button>
          <Button variant="contained" onClick={submitKycReview}>Save</Button>
        </DialogActions>
      </Dialog>

      <SipTraceModal callId={traceId} open={traceOpen} onClose={() => setTraceOpen(false)} />
    </Box>
  );
};
